import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import ConfirmDialog from '../ui/ConfirmDialog';
import type { ParentAssignmentCheck } from '../../types';
import { alsoAssignQuestionText } from './taskRowModel';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { withoutHiddenProject } from '../../utils/directUnderOte';

/**
 * P-06/C (D-59) — "<שם העובד> שויכו למשימה. לשייך גם לאבן הדרך '<שם>' ולפרויקט
 * '<שם>'?" [כן] / [לא], asked of whoever assigns (create, edit, "הוספת עובד").
 * The subject is the employee, not the task; there is no gender data on
 * employees, so the Hebrew is the neutral plural form (QA-R3 #5).
 *
 * `ask(where, agents)` reads the server's check (the milestone, the project
 * — project / development only — and who is already in each). No question
 * when every worker is already in both, or when the task has neither (service
 * contract / hour bank / לל"ח): it resolves `false` at once. Otherwise the
 * dialog resolves `true` on "כן", `false` on "לא" / close. A failed check
 * never blocks the save: it resolves `false` (the default — no parents).
 */
export function useAlsoAssignQuestion() {
  const { t } = useTranslation();
  // D-274b: the hidden "עבודה פנימית" project is never named in the question (same query key as the task picker).
  const isStaff = useAuthStore((s) => s.user?.role === 'admin' || s.user?.role === 'agent');
  const { data: internalRes } = useQuery({
    queryKey: ['engagements', 'internal-work'],
    queryFn: () => api.getInternalWork(),
    staleTime: 5 * 60_000,
    enabled: isStaff,
  });
  const hiddenProjectId = internalRes?.data?.project_id ?? null;
  const [pending, setPending] = useState<{ check: ParentAssignmentCheck; names: string[] } | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const ask = useCallback(async (
    where: { ticketId?: string; milestoneId?: string; projectId?: string },
    agentIds: string[],
    /** Names of the workers by id (for the text), when known. */
    nameOf?: (id: string) => string | undefined,
  ): Promise<boolean> => {
    const ids = [...new Set(agentIds.filter(Boolean))];
    if (ids.length === 0) return false;
    if (!where.ticketId && !where.milestoneId && !where.projectId) return false;
    let check: ParentAssignmentCheck | undefined;
    try {
      const raw = (await api.getParentAssignmentCheck({ ...where, agentIds: ids })).data;
      check = raw ? withoutHiddenProject(raw, hiddenProjectId) : raw;
    } catch {
      return false;
    }
    if (!check?.ask) return false;
    const missing = check.agents.filter((a) => (check!.milestone && !a.in_milestone) || (check!.project && !a.in_project));
    const names = missing.map((a) => nameOf?.(a.id) || a.name || '').filter(Boolean);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setPending({ check: check!, names });
    });
  }, [hiddenProjectId]);

  const answer = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setPending(null);
  };

  const dialog: ReactNode = pending ? (
    <ConfirmDialog
      open
      title={t('taskAssign.alsoTitle')}
      // Only the parents someone is still missing from are named.
      description={alsoAssignQuestionText(
        t,
        pending.names,
        pending.check.milestone && pending.check.agents.some((a) => !a.in_milestone) ? pending.check.milestone.name : null,
        pending.check.project && pending.check.agents.some((a) => !a.in_project) ? pending.check.project.name : null,
      )}
      confirmLabel={t('taskAssign.yes')}
      cancelLabel={t('taskAssign.no')}
      onConfirm={() => answer(true)}
      onCancel={() => answer(false)}
    />
  ) : null;

  return { ask, dialog };
}
